import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    ChevronDown,
    ChevronRight,
    Heart,
    PackageCheck,
    Star,
    Truck,
    Zap,
} from "lucide-react";
import { useWishlist } from "../../../../../context/WishlistContext";
import WomenProductOptionsModal from "./WomenProductOptionsModal/WomenProductOptionsModal";
import {
    FashionProductItem,
    mapBrowseProductToFashion,
    formatPrice,
} from "../fashionTypes";
import "./WomenFashionSection.css";

const PRODUCT_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || ""}${process.env.REACT_APP_ECOMMERCE_PERFIX || ""}`;

const womenFashionCache = new Map<string, FashionProductItem[]>();

interface WomenFashionSectionProps {
    superCategory?: string;
    categoryName?: string;
    limit?: number;
}

const WomenFashionSection: React.FC<WomenFashionSectionProps> = ({
    superCategory = "Women",
    categoryName = "Women",
    limit = 8,
}) => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const { isItemWishlisted, toggleWishlist: toggleWishlistContext } = useWishlist();

    const [products, setProducts] = useState<FashionProductItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [selectedColors, setSelectedColors] = useState<Record<string | number, number>>({});
    const [selectedProduct, setSelectedProduct] = useState<FashionProductItem | null>(null);

    // Lazy load when scrolled near viewport
    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: "240px 0px" }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Fetch dynamic products from browse API
    useEffect(() => {
        if (!isVisible) return;

        const cacheKey = `${superCategory}-${limit}`;
        const cached = womenFashionCache.get(cacheKey);
        if (cached) {
            setProducts(cached);
            setHasLoaded(true);
            return;
        }

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                // Try superCategory=Women
                const res = await axios.get(
                    `${PRODUCT_BASE_URL}/e-commerce/catalog/categories/browse`,
                    {
                        params: {
                            superCategory,
                            limit,
                            sort: "relevance",
                        },
                    }
                );

                let rawItems = res.data?.data?.products || [];

                // If superCategory returned 0, try searching with 'q'
                if (rawItems.length === 0) {
                    const searchRes = await axios.get(
                        `${PRODUCT_BASE_URL}/e-commerce/catalog/categories/browse`,
                        {
                            params: {
                                q: superCategory || categoryName || "Women",
                                limit,
                                sort: "relevance",
                            },
                        }
                    );
                    rawItems = searchRes.data?.data?.products || [];
                }

                if (!cancelled) {
                    if (rawItems.length > 0) {
                        const mapped = rawItems.map((item: any, idx: number) =>
                            mapBrowseProductToFashion(item, "women", idx)
                        );
                        womenFashionCache.set(cacheKey, mapped);
                        setProducts(mapped);
                    } else {
                        womenFashionCache.set(cacheKey, []);
                        setProducts([]);
                    }
                    setHasLoaded(true);
                }
            } catch {
                if (!cancelled) {
                    setProducts([]);
                    setHasLoaded(true);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [isVisible, superCategory, limit]);

    const selectColor = (productId: string | number, colorIndex: number) => {
        setSelectedColors((prev) => ({
            ...prev,
            [productId]: colorIndex,
        }));
    };

    const handleWishlistToggle = (e: React.MouseEvent, product: FashionProductItem) => {
        e.stopPropagation();
        if (toggleWishlistContext) {
            toggleWishlistContext(String(product.id));
        }
    };

    const handleViewAll = () => {
        navigate(`/Categories/${encodeURIComponent(categoryName)}`);
    };

    const handleProductClick = (product: FashionProductItem) => {
        const targetId = product._id || product.id || product.ssin;
        navigate(`/DetailPage/${targetId}`, {
            state: {
                product: {
                    ...product,
                    category: "Women Fashion",
                    breadcrumb: [
                        "Home",
                        "Women",
                        "Fashion & Accessories",
                        product.title,
                    ],
                },
            },
        });
    };

    if (!isVisible) {
        return <div ref={sectionRef} style={{ minHeight: 1 }} aria-hidden="true" />;
    }

    // If data was fetched and nothing was found, return null in place of the whole component
    if (hasLoaded && products.length === 0) {
        return null;
    }

    if (loading && products.length === 0) {
        return (
            <div ref={sectionRef} className="container-fluid px-3 py-3">
                <div className="d-flex gap-3 overflow-hidden">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div
                            key={i}
                            className="flex-shrink-0"
                            style={{
                                width: 260,
                                height: 420,
                                borderRadius: 16,
                                background: "#f0f2f5",
                            }}
                        />
                    ))}
                </div>
            </div>
        );
    }

    if (products.length === 0) {
        return null;
    }

    return (
        <div ref={sectionRef}>
            <section className="women-fashion">
                <div className="women-fashion-container">
                    <div className="women-fashion-header">
                        <div>
                            <span className="women-fashion-eyebrow">
                                CURATED FOR HER
                            </span>
                            <h2>Women Fashion & Accessories</h2>
                            <p>
                                Elegant styles, premium edits and everyday essentials selected for you.
                            </p>
                        </div>

                        <button
                            type="button"
                            className="women-fashion-view-all"
                            onClick={handleViewAll}
                        >
                            View all
                            <ChevronRight
                                size={16}
                                strokeWidth={2}
                            />
                        </button>
                    </div>

                    <div className="women-fashion-grid">
                        {products.map((product) => {
                            const discount = Math.round(
                                ((product.mrp - product.price) /
                                    product.mrp) *
                                    100
                            );

                            const isWishlisted = isItemWishlisted(String(product.id));
                            const selectedColorIndex =
                                selectedColors[product.id] ?? 0;
                            const activeColor =
                                product.colors[selectedColorIndex] ||
                                product.colors[0];
                            const displayedImage =
                                activeColor?.image || product.image;

                            return (
                                <article
                                    className="women-product-card"
                                    key={product.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={(event) => {
                                        const target = event.target as HTMLElement;
                                        if (target.closest("button")) return;
                                        handleProductClick(product);
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            handleProductClick(product);
                                        }
                                    }}
                                >
                                    <div className="women-product-image-wrapper">
                                        <span className="women-product-badge">
                                            {product.badge}
                                        </span>

                                        <button
                                            type="button"
                                            className={`women-product-wishlist ${isWishlisted
                                                ? "active"
                                                : ""
                                                }`}
                                            onClick={(e) =>
                                                handleWishlistToggle(e, product)
                                            }
                                            aria-label={
                                                isWishlisted
                                                    ? "Remove from wishlist"
                                                    : "Add to wishlist"
                                            }
                                        >
                                            <Heart
                                                size={18}
                                                strokeWidth={2}
                                                fill={
                                                    isWishlisted
                                                        ? "currentColor"
                                                        : "none"
                                                }
                                            />
                                        </button>

                                        <img
                                            src={displayedImage}
                                            alt={product.title}
                                            className="women-product-image"
                                            onError={(e) => {
                                                e.currentTarget.src =
                                                    "/assets/images/products/women-fas/women-1.png";
                                            }}
                                        />
                                    </div>

                                    <div className="women-product-info">
                                        {product.colors.length > 0 && (
                                            <div className="women-product-colors">
                                                <div className="women-product-color-list">
                                                    {product.colors.slice(0, 4).map(
                                                        (color, index) => (
                                                            <button
                                                                type="button"
                                                                key={color.name}
                                                                className={`women-product-color ${selectedColorIndex ===
                                                                    index
                                                                    ? "active"
                                                                    : ""
                                                                    }`}
                                                                title={color.name}
                                                                aria-label={`Select ${color.name}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    selectColor(
                                                                        product.id,
                                                                        index
                                                                    );
                                                                }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        backgroundColor:
                                                                            color.value,
                                                                    }}
                                                                />
                                                            </button>
                                                        )
                                                    )}
                                                </div>

                                                {product.extraColors > 0 && (
                                                    <button
                                                        type="button"
                                                        className="women-product-more-colors"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedProduct(product);
                                                        }}
                                                    >
                                                        +{product.extraColors} more
                                                    </button>
                                                )}
                                            </div>
                                        )}

                                        <h3>{product.title}</h3>

                                        <p className="women-product-description">
                                            {product.description}
                                        </p>

                                        <div className="women-product-rating">
                                            <strong>
                                                {product.rating.toFixed(1)}
                                            </strong>

                                            <span className="women-product-stars">
                                                {[1, 2, 3, 4, 5].map(
                                                    (star) => (
                                                        <Star
                                                            key={star}
                                                            size={13}
                                                            strokeWidth={1.8}
                                                            fill={
                                                                star <=
                                                                    Math.round(
                                                                        product.rating
                                                                    )
                                                                    ? "currentColor"
                                                                    : "none"
                                                                }
                                                        />
                                                    )
                                                )}
                                            </span>

                                            <span className="women-product-review-count">
                                                ({product.reviews})
                                            </span>
                                        </div>

                                        <div
                                            className={`women-product-deal ${product.dealType}`}
                                        >
                                            <span className="women-product-deal-dot" />
                                            <strong>
                                                {product.deal}
                                            </strong>
                                        </div>

                                        <div className="women-product-pricing">
                                            <div className="women-product-price-main">
                                                <strong>
                                                    <sup>₹</sup>
                                                    {formatPrice(
                                                        product.price
                                                    )}
                                                </strong>

                                                {discount > 0 && (
                                                    <span className="women-product-discount">
                                                        {discount}% OFF
                                                    </span>
                                                )}
                                            </div>

                                            {product.mrp > product.price && (
                                                <div className="women-product-price-secondary">
                                                    <span>MRP</span>

                                                    <del>
                                                        ₹
                                                        {formatPrice(
                                                            product.mrp
                                                        )}
                                                    </del>

                                                    <span className="women-product-saving">
                                                        Save ₹
                                                        {formatPrice(
                                                            product.mrp -
                                                            product.price
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <div
                                            className={`women-product-delivery ${product.deliveryType}`}
                                        >
                                            {product.deliveryType ===
                                                "delivery" && (
                                                    <Truck
                                                        size={15}
                                                        strokeWidth={1.9}
                                                    />
                                                )}

                                            {product.deliveryType ===
                                                "fast" && (
                                                    <Zap
                                                        size={15}
                                                        strokeWidth={2}
                                                    />
                                                )}

                                            {product.deliveryType ===
                                                "stock" && (
                                                    <PackageCheck
                                                        size={15}
                                                        strokeWidth={1.9}
                                                    />
                                                )}

                                            <span>
                                                {product.delivery}
                                            </span>
                                        </div>

                                        <button
                                            type="button"
                                            className="women-product-options"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedProduct(product);
                                            }}
                                        >
                                            Select Options
                                            <ChevronDown
                                                size={15}
                                                strokeWidth={2}
                                            />
                                        </button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            <WomenProductOptionsModal
                product={selectedProduct}
                onClose={() => setSelectedProduct(null)}
            />
        </div>
    );
};

export default WomenFashionSection;